import { Injectable, OnDestroy } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, ActivatedRoute } from '@angular/router';
import { KeycloakService, KeycloakAuthGuard } from 'keycloak-angular';
import { Store, select } from '@ngrx/store';
import { takeUntil, delay } from 'rxjs/operators';
import { Subject } from 'rxjs';

import * as fromMain from '@app/main/main.selectors';

import * as GuardActions from '@app/main/store/actions/guard-actions/guard.action';
import * as AuthActions from '@app/auth/store/actions/auth.action';
import { AppRoutes } from './../app.routing';
import { QueryParamatersService, ESTABLISHMENT_ID } from '../core/services/queryParameters.service';

@Injectable()
export class AppKeycloakGuard extends KeycloakAuthGuard implements OnDestroy {
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    protected router: Router,
    protected activeRoute: ActivatedRoute,
    protected keycloakAngular: KeycloakService,
    private mainStore: Store<fromMain.MainState>,
    private queryParamatersService: QueryParamatersService
  ) {
    super(router, keycloakAngular);
  }

  isAccessAllowed(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return new Promise(async resolve => {
      this.checkGoogleRoute(state.url);
      const hash = window.location.hash;

      if (hash && hash.includes('&iss=')) {
        const cleanHash = hash.split('&iss=')[0];
        window.location.hash = cleanHash;
        return resolve(false);
      }

      const showStaticPages = this.queryParamatersService.isOpenRoute() && !this.authenticated;
      if (showStaticPages) {
        return resolve(true);
      }

      const showDynamicRoutes = this.queryParamatersService.isOpenRoute(state.url) && this.authenticated;
      if (showDynamicRoutes) {
        this.navigateToAuthenticatedPublicPage(state);
        return resolve(false);
      }

      const establishmentId: string = localStorage.getItem(ESTABLISHMENT_ID);

      if (establishmentId) {
        this.mainStore.dispatch(new GuardActions.SetEstablismentId({ establishmentId }));
      }

      if (!this.authenticated) {
        const path = state.url || '/main/dashboard';
        localStorage.setItem('redirect_url', path);

        const redirectUri = window.location.origin + window.location.pathname + '#' + path;

        try {
          await this.keycloakAngular.login({ redirectUri });
        } catch (error) {
          console.error('[Guard] Keycloak login failed:', error);
        }
        return resolve(false);
      }

      const redirectUrl = localStorage.getItem('redirect_url');

      if (redirectUrl && redirectUrl.replace(/^#/, '') !== state.url && !localStorage.getItem('redirect_done')) {
        localStorage.setItem('redirect_done', 'true');
        this.router.navigateByUrl(state.url);
        return resolve(false);
      }

      if (state.url === '/main/acc_settings') {
        this.mainStore.dispatch(new GuardActions.OpenSettingsPage());
      } else {
        this.mainStore.dispatch(new GuardActions.CloseSettingsPage());
      }

      this.mainStore.pipe(delay(0), select(fromMain.getTermsConditionsState), takeUntil(this.unsubscribe$)).subscribe(terms => {
        if (terms?.hasBootstrapError) {
          this.mainStore.dispatch(new AuthActions.NavMenuLayoutHide());
        }
      });

      this.mainStore.pipe(delay(0), select(fromMain.getSelectedBusiness), takeUntil(this.unsubscribe$)).subscribe(dashboard => {
        if (!dashboard || state.url === '/main/upgrade') {
          this.mainStore.dispatch(new AuthActions.NavMenuLayoutHide());
        } else {
          this.mainStore.dispatch(new AuthActions.NavMenuLayoutShow());
        }
      });
      const requiredRoles = route.data.roles;
      if (!requiredRoles || requiredRoles.length === 0) {
        return resolve(true);
      }
      if (!this.roles || this.roles.length === 0) {
        return resolve(false);
      }
      const granted = requiredRoles.some(role => this.roles.includes(role));
      return resolve(granted);
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private navigateToAuthenticatedPublicPage(routeSnapshot: RouterStateSnapshot): void {
    const queryParams = routeSnapshot?.root?.queryParams || {};
    const routeParams: string[] = routeSnapshot.url.split('?');
    const route = routeParams.length ? routeParams[0] : 'dashboard';
    const navigateTo = route.replace(AppRoutes.PUBLIC, AppRoutes.MAIN);
    this.router.navigate([navigateTo], { queryParams });
  }

  /**
   * Checks if the app url has /google/finish route
   * If it has, then the window is automatically closed
   */
  private checkGoogleRoute(route: string): void {
    const browserRoute: string = window.location.href;
    const hasGoogleFinishRoute: boolean = route.includes('/google/finish') || browserRoute.includes('/google/finish');
    if (hasGoogleFinishRoute) {
      window.close();
    }
  }
}
